import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useActiveAccount } from 'thirdweb/react';
import { useCreateChallenge, useJoinChallenge } from '../hooks/useChallenge';
import GasBanner from '../components/GasBanner';
import './OneVsOne.css';

const API         = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const USDC_ADDRESS = process.env.REACT_APP_USDC_ADDRESS || '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';

// Tarkov Arena is the hero game for 1v1 — most reliable stats, tightest match
// window, and the most engaged player community for ezegames.
const GAMES = [
  { value: 'tarkov',   label: '☠️ Tarkov Arena',  field: 'tarkovPlayerId', placeholder: 'Tarkov Arena username',  winMins: 25 },
  { value: 'fortnite', label: '⚡ Fortnite',     field: 'epicPlayerId',   placeholder: 'Epic username',          winMins: 30 },
  { value: 'cs2',      label: '🔫 CS2 Premier',   field: 'steamPlayerId',  placeholder: 'Steam64 ID',             winMins: 60 },
  { value: 'apex',     label: '🔴 Apex Legends',  field: 'apexPlayerId',   placeholder: 'EA / Origin username',   winMins: 30 },
];

function formatUSDC(microUsdc) {
  if (!microUsdc) return '$0';
  return `$${(Number(microUsdc) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STEP_LABELS = {
  deploying:   '⏳ Deploying escrow on Polygon...',
  approving:   'Step 1/3: Approving USDC...',
  funding:     'Step 2/3: Funding escrow...',
  confirming:  'Step 3/3: Finalizing challenge...',
  joining:     'Step 2/3: Joining escrow on-chain...',
};

// ── Create panel ─────────────────────────────────────────────────────────────
function CreatePanel({ onCreated }) {
  const account = useActiveAccount();
  const { createChallenge, state, reset } = useCreateChallenge();

  const [game, setGame]           = useState('tarkov');
  const [betAmt, setBetAmt]       = useState('1');
  const [playerId, setPlayerId]   = useState('');
  const [formError, setFormError] = useState('');
  const [created, setCreated]     = useState(null);

  const selectedGame = GAMES.find(g => g.value === game);
  const betNum       = parseFloat(betAmt) || 0;
  const winnerPrize  = betNum * 2 * 0.95;

  const busy = ['deploying','approving','funding','confirming'].includes(state.step);

  async function handleCreate(e) {
    e.preventDefault();
    if (!account) { setFormError('Connect your wallet first'); return; }
    if (!playerId.trim()) { setFormError(`${selectedGame.placeholder} is required`); return; }
    if (betNum < 0.01) { setFormError('Minimum bet is $0.01 USDC'); return; }
    setFormError('');

    try {
      const result = await createChallenge({
        game,
        betAmountUsdc: betNum,
        usdcAddress:   USDC_ADDRESS,
        gamePlayerId:  playerId.trim(),
        gameField:     selectedGame.field,
      });
      setCreated(result);
      onCreated?.();
    } catch (err) {
      // The hook already sets state.error — don't duplicate it in formError
      // Only set formError for backend HTTP errors (not chain errors)
      const backendMsg = err?.response?.data?.error;
      if (backendMsg) setFormError(backendMsg);
      // Chain/thirdweb errors are shown via state.error below
    }
  }

  function handleNew() { setCreated(null); reset(); setPlayerId(''); setBetAmt('1'); setFormError(''); }

  function copyLink() {
    const url = `${window.location.origin}/1v1?challenge=${created.challengeId}`;
    navigator.clipboard.writeText(url).catch(() => {});
    alert('Link copied! Send it to your opponent.');
  }

  if (created) {
    return (
      <div className="onevone__create-panel">
        <div className="onevone__create-title">⚔️ <span>Challenge Created!</span></div>
        <div className="onevone__created">
          <div className="onevone__created-title">✅ Your escrow is live</div>
          <div className="onevone__created-sub">Challenge ID — send to your opponent:</div>
          <div className="onevone__created-id">#{created.challengeId}</div>
          <div className="onevone__created-sub">
            Your {formatUSDC(created.challenge?.bet_amount)} is locked in escrow.<br />
            When your opponent joins, the {selectedGame?.winMins}-minute match window starts automatically.
          </div>
          <button className="onevone__share-btn" onClick={copyLink}>
            📋 Copy challenge link
          </button>
          <button className="onevone__new-btn" onClick={handleNew}>
            + Create another challenge
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="onevone__create-panel">
      <div className="onevone__create-title">⚔️ Create a <span>1v1</span></div>
      <form onSubmit={handleCreate}>
        <div className="onevone__field">
          <label className="onevone__label">Game</label>
          <select className="onevone__select" value={game} onChange={e => { setGame(e.target.value); setPlayerId(''); }}>
            {GAMES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>

        <div className="onevone__field">
          <label className="onevone__label">{selectedGame.placeholder}</label>
          <input
            className="onevone__input"
            placeholder={selectedGame.placeholder}
            value={playerId}
            onChange={e => setPlayerId(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="onevone__field">
          <label className="onevone__label">Bet Amount (USDC)</label>
          <div className="onevone__bet-presets">
            {['0.01','1','5','10','25','50','100'].map(v => (
              <button
                key={v}
                type="button"
                className={`onevone__bet-preset${betAmt === v ? ' onevone__bet-preset--active' : ''}${v === '0.01' ? ' onevone__bet-preset--test' : ''}`}
                onClick={() => setBetAmt(v)}
                disabled={busy}
              >
                {v === '0.01' ? '🧪 $0.01' : `$${v}`}
              </button>
            ))}
          </div>
          <div className="onevone__bet-row">
            <span className="onevone__bet-prefix">$</span>
            <input
              className="onevone__bet-input"
              type="number"
              min="0.01"
              step="0.01"
              value={betAmt}
              onChange={e => setBetAmt(e.target.value)}
              disabled={busy}
              placeholder="1"
            />
            <span className="onevone__bet-suffix">USDC</span>
          </div>
        </div>

        {betNum >= 0.01 && (
          <div className="onevone__prize-preview">
            <div>
              <div className="onevone__prize-preview-label">Winner takes</div>
              <div className="onevone__prize-preview-fee">5% platform fee</div>
            </div>
            <div className="onevone__prize-preview-value">
              ${winnerPrize.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        )}

        {busy && (
          <div className="onevone__step-hint">{STEP_LABELS[state.step]}</div>
        )}
        {/* Single error display: prefer hook's parsed chain error, fall back to formError */}
        {(state.error || formError) && (
          <div className="onevone__error">{state.error || formError}</div>
        )}

        <button type="submit" className="onevone__create-btn" disabled={busy || !account}>
          {!account ? 'Connect Wallet to Create' : busy ? 'Processing...' : '⚔️ Create Challenge'}
        </button>
      </form>
    </div>
  );
}

// ── Join modal ────────────────────────────────────────────────────────────────
function JoinModal({ challenge, onClose, onJoined }) {
  const account = useActiveAccount();
  const { joinChallenge, state, reset } = useJoinChallenge();
  const selectedGame = GAMES.find(g => g.value === challenge.game) || GAMES[0];

  const [playerId, setPlayerId] = useState('');
  const [error, setError]       = useState('');
  const [joined, setJoined]     = useState(false);

  const busy = ['approving','joining','confirming'].includes(state.step);

  async function handleJoin(e) {
    e.preventDefault();
    if (!playerId.trim()) { setError(`${selectedGame.placeholder} is required`); return; }
    if (!account)         { setError('Connect your wallet first'); return; }
    setError('');
    try {
      await joinChallenge({ challenge, gamePlayerId: playerId.trim(), gameField: selectedGame.field });
      setJoined(true);
      onJoined?.();
    } catch (err) {
      // Chain errors are already shown via state.error from the hook
      const backendMsg = err?.response?.data?.error;
      if (backendMsg) setError(backendMsg);
    }
  }

  return (
    <div className="onevone__modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="onevone__modal">
        {joined ? (
          <>
            <div className="onevone__modal-success-title">✅ You're in! Match window is live.</div>
            <p className="onevone__modal-success-detail">
              The {selectedGame.winMins}-minute kill window has started. Launch {selectedGame.label.split(' ').slice(1).join(' ')} and
              get as many kills as possible. Winner takes {formatUSDC(String(Number(challenge.bet_amount) * 2 * 0.95))} — paid
              instantly on Polygon.
            </p>
            <button className="onevone__modal-close-btn" onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <div className="onevone__modal-title">⚔️ Join Challenge #{challenge.id}</div>
            <p className="onevone__modal-sub">vs {challenge.creatorHandle} · {selectedGame.label}</p>

            <div className="onevone__modal-detail">
              <div className="onevone__modal-detail-row">
                <span className="onevone__modal-detail-key">Your bet</span>
                <span className="onevone__modal-detail-value">{formatUSDC(challenge.bet_amount)}</span>
              </div>
              <div className="onevone__modal-detail-row">
                <span className="onevone__modal-detail-key">Opponent</span>
                <span className="onevone__modal-detail-value">{challenge.creatorHandle}</span>
              </div>
              <div className="onevone__modal-detail-row">
                <span className="onevone__modal-detail-key">Window</span>
                <span className="onevone__modal-detail-value">{selectedGame.winMins} min</span>
              </div>
            </div>

            <div className="onevone__modal-prize">
              <div className="onevone__modal-prize-label">Winner takes (95%)</div>
              <div className="onevone__modal-prize-value">{formatUSDC(challenge.winnerPrize)}</div>
            </div>

            <form onSubmit={handleJoin}>
              <div className="onevone__field">
                <label className="onevone__label">{selectedGame.placeholder}</label>
                <input
                  className="onevone__input"
                  placeholder={selectedGame.placeholder}
                  value={playerId}
                  onChange={e => setPlayerId(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </div>
              {busy && <div className="onevone__modal-step">{STEP_LABELS[state.step]}</div>}
              {error && <div className="onevone__error">{error}</div>}
              <div className="onevone__modal-btns">
                <button type="button" className="onevone__modal-cancel-btn" onClick={onClose} disabled={busy}>Cancel</button>
                <button type="submit" className="onevone__modal-confirm-btn" disabled={busy || !account}>
                  {!account ? 'Connect Wallet' : busy ? 'Processing...' : `Pay ${formatUSDC(challenge.bet_amount)} & Join`}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Challenge card ────────────────────────────────────────────────────────────
function ChallengeCard({ challenge, onJoin }) {
  const account = useActiveAccount();
  const isOwn   = account?.address?.toLowerCase() === challenge.creator_wallet?.toLowerCase();

  return (
    <div className="onevone__card">
      <div className="onevone__card-top">
        <div className="onevone__card-left">
          <div className="onevone__card-id">Challenge #{challenge.id}</div>
          <div className="onevone__card-creator">{challenge.creatorHandle}</div>
          <div className={`onevone__card-game onevone__card-game--${challenge.game}`}>
            {challenge.game === 'fortnite' ? '⚡ Fortnite'
              : challenge.game === 'apex'    ? '🔴 Apex'
              : challenge.game === 'cs2'     ? '🔫 CS2'
              : '☠️ Tarkov'}
          </div>
        </div>
        <div className="onevone__card-right">
          <div className="onevone__card-bet">Each pays {formatUSDC(challenge.bet_amount)}</div>
          <div className="onevone__card-prize">{formatUSDC(challenge.winnerPrize)}</div>
          <div className="onevone__card-prize-sub">winner takes 95%</div>
        </div>
      </div>

      <div className="onevone__card-vs">
        <div className="onevone__card-player">{challenge.creatorHandle}</div>
        <div className="onevone__card-vs-badge">VS</div>
        <div className="onevone__card-player onevone__card-open-slot">Open slot</div>
      </div>

      <button
        className="onevone__card-join-btn"
        onClick={() => onJoin(challenge)}
        disabled={isOwn}
      >
        {isOwn ? 'Your challenge — share the link' : `Accept · Pay ${formatUSDC(challenge.bet_amount)}`}
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function OneVsOne() {
  const [searchParams]                        = useSearchParams();
  const [challenges, setChallenges]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [lastRefresh, setLastRefresh]         = useState(null);
  const [gameFilter, setGameFilter]           = useState('all');
  const [joiningChallenge, setJoiningChallenge] = useState(null);

  // If URL has ?challenge=ID, auto-open join modal for that challenge
  const deepLinkId = searchParams.get('challenge');

  const fetchChallenges = useCallback(async () => {
    try {
      const params = gameFilter !== 'all' ? `?game=${gameFilter}` : '';
      const { data } = await axios.get(`${API}/challenge/open${params}`);
      setChallenges(data.challenges);
      setLastRefresh(new Date());

      // Auto-open deep-linked challenge
      if (deepLinkId) {
        try {
          const { data: cData } = await axios.get(`${API}/challenge/${deepLinkId}`);
          if (cData.challenge?.status === 'open') {
            setJoiningChallenge(cData.challenge);
          }
        } catch {}
      }
    } catch {}
    finally { setLoading(false); }
  }, [gameFilter, deepLinkId]);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);
  useEffect(() => {
    const id = setInterval(fetchChallenges, 15_000);
    return () => clearInterval(id);
  }, [fetchChallenges]);

  const filtered = gameFilter === 'all'
    ? challenges
    : challenges.filter(c => c.game === gameFilter);

  return (
    <div className="onevone">
      <Helmet>
        <title>1v1 Challenges — Bet USDC on Yourself | EZGAME</title>
        <meta name="description" content="Challenge anyone to a 1v1 in Fortnite, Apex, CS2 or Tarkov Arena. Bet USDC, most kills wins 95% — instant payout on Polygon." />
        <link rel="canonical" href="https://www.ezegames.live/1v1" />
        <meta property="og:title" content="1v1 Kill Challenges — Bet USDC on Yourself | EZGAME" />
        <meta property="og:description" content="Create a 1v1 challenge in your favorite FPS, bet USDC on yourself. Winner takes 95%. Instant payout on Polygon." />
        <meta property="og:url" content="https://www.ezegames.live/1v1" />
      </Helmet>

      {/* Hero */}
      <div className="onevone__hero">
        <div className="onevone__eyebrow">⚔️ 1v1 Challenges</div>
        <h1 className="onevone__hero-title">
          Bet on Yourself.<br /><span className="onevone__fire-text">Winner Takes All.</span>
        </h1>
        <p className="onevone__hero-sub">
          Challenge a friend or a random player. Pick your game, set the bet — minimum $0.01 USDC, no maximum.
          Most kills wins 95% of the pot. Instant payout on Polygon.
        </p>
        <div className="onevone__how-strip">
          {[
            { icon: '🦊', text: 'Connect wallet' },
            { icon: '⚔️', text: 'Create or join a challenge' },
            { icon: '💰', text: 'Both players pay into escrow' },
            { icon: '🎮', text: 'Play — most kills wins' },
            { icon: '⚡', text: 'Instant USDC payout' },
          ].map(s => (
            <div key={s.text} className="onevone__how-item">
              <span className="onevone__how-icon">{s.icon}</span>
              <span>{s.text}</span>
            </div>
          ))}
        </div>
      </div>

      <GasBanner />

      <div className="onevone__main">
        {/* Left: create */}
        <CreatePanel onCreated={fetchChallenges} />

        {/* Right: open challenges */}
        <div className="onevone__list-panel">
          <div className="onevone__list-header">
            <h2 className="onevone__list-title">Open <span>Challenges</span></h2>
            {lastRefresh && (
              <span className="onevone__refresh-badge">Updated {lastRefresh.toLocaleTimeString()}</span>
            )}
          </div>

          <div className="onevone__filter-row">
            {['all','fortnite','apex','cs2','tarkov'].map(g => (
              <button
                key={g}
                className={`onevone__filter-btn${gameFilter === g ? ' onevone__filter-btn--active' : ''}`}
                onClick={() => setGameFilter(g)}
              >
                {g === 'all' ? '🎮 All' : g === 'fortnite' ? '⚡ Fortnite' : g === 'apex' ? '🔴 Apex' : g === 'cs2' ? '🔫 CS2' : '☠️ Tarkov'}
              </button>
            ))}
          </div>

          {loading && <div className="onevone__list-empty">Loading challenges...</div>}

          {!loading && filtered.length === 0 && (
            <div className="onevone__list-empty">
              No open challenges right now — be the first to create one! 👈
            </div>
          )}

          {filtered.map(c => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              onJoin={setJoiningChallenge}
            />
          ))}
        </div>
      </div>

      {/* Join modal */}
      {joiningChallenge && (
        <JoinModal
          challenge={joiningChallenge}
          onClose={() => setJoiningChallenge(null)}
          onJoined={() => { setJoiningChallenge(null); fetchChallenges(); }}
        />
      )}
    </div>
  );
}
